import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PauseCircle } from 'phosphor-react';
import exact from 'prop-types-exact';
import { PlayCircle, Prohibit } from '@phosphor-icons/react';
import PlaceholderImage from '../../../common/placeholderImage/PlaceholderImage';
import Button from '../../../common/button/Button';
import DownloadButton from '../../../common/DownloadButton/DownloadButton';

const IMG_MEDIA_URL = 'https://a1media.antena1.com.br/image/album/200x200/';
const DEFAULT_ALBUM = `${IMG_MEDIA_URL}defaultAlbum.jpg`;

const MusicsCard = ({
    currentMusicId, playMediaPlayer, pauseMediaPlayer,
    setPlaylist, playerStatus, playlistType, isLive,
    useFilteredMusics, playlistData, filteredMusics, music, showDownloadButton,
    musicIndex,
}) => {
    const isCurrentMusic = useMemo(() => (
        currentMusicId === music.musicId && isLive === 'otherMedia'
    ), [currentMusicId, music, isLive]);

    const handlingPause = useCallback(() => {
        pauseMediaPlayer();
    }, [pauseMediaPlayer]);

    const setPlaylistHandler = useCallback(() => {
        const itemMusicId = music.musicId;
        const treatedPlaylist = [];
        let musicIndexInPlaylist = 0;
        let numItems = 0;
        const playlistToBeSet = !useFilteredMusics ? playlistData : filteredMusics;

        playlistToBeSet.forEach((track) => {
            if (track.musicTrack) {
                treatedPlaylist.push(track);

                numItems += 1;

                if (track.musicId === itemMusicId) {
                    musicIndexInPlaylist = numItems - 1;
                }
            }
        });

        setPlaylist(playlistType, treatedPlaylist, musicIndexInPlaylist, itemMusicId);
    }, [music, playlistData, filteredMusics, useFilteredMusics, setPlaylist, playlistType]);

    const handlingPlay = useCallback(() => {
        if (isCurrentMusic && playerStatus === 'paused') {
            playMediaPlayer();
        }

        setPlaylistHandler();
    }, [isCurrentMusic, playerStatus, playMediaPlayer, setPlaylistHandler]);

    const {
        musicName, musicSlug, musicTrack, albumName, artistName,
        artistSlug, albumCover, itunesCover,
    } = music;

    const imageSrc = albumCover ? IMG_MEDIA_URL + albumCover : (itunesCover || DEFAULT_ALBUM);
    const play = isCurrentMusic && playerStatus === 'playing';

    const location = useLocation();

    return (
        <article className="tw-bg-slate-200">
            <div className="tw-flex tw-items-center">
                <div className="tw-w-1/3">
                    <figure className="tw-m-0">
                        <PlaceholderImage placeholderWidth="200px" placeholderHeight="200px" src={imageSrc} alt={`Album ${albumName}`} />
                    </figure>
                    {(location.pathname === '/as-100-mais' || location.pathname === '/as-10-mais') && (
                        <p className="tw-absolute tw-p-3 -tw-bottom-6 tw-left-1 tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-700 tw-text-white tw-font-bold">
                            {musicIndex}
                        </p>
                    )}
                </div>

                <div className="tw-flex tw-items-center tw-w-2/3">
                    <div className="tw-flex tw-flex-col tw-w-4/5 tw-p-1 md:tw-p-3 lg:tw-p-4">
                        {artistSlug ? (
                            <React.Fragment>
                                <Button
                                    to={`/musicas/${musicSlug}+${artistSlug}`}
                                    className=""
                                    title={`Ir para a música ${musicName}`}
                                >
                                    <p className="limitText2 tw-text-slate-900 tw-font-bold tw-text-sm lg:tw-text-lg tw-no-underline hover:tw-no-underline">
                                        {musicName}
                                    </p>
                                </Button>
                                <Button
                                    to={`/artistas/${artistSlug}`}
                                    className="tw-text-gray-500 tw-text-xs lg:tw-text-base tw-no-underline hover:tw-no-underline"
                                    title={`Ir para o artista ${artistName}`}
                                >
                                    {artistName.substring(0, 40)}
                                    {' '}
                                    {artistName.trim().length > 40 ? '...' : ''}
                                </Button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div>
                                    <p className="limitText2 tw-text-slate-900 tw-font-bold tw-text-sm lg:tw-text-lg tw-no-underline hover:tw-no-underline">
                                        {musicName}
                                    </p>
                                </div>
                                <div className="tw-text-gray-500 tw-text-xs lg:tw-text-base tw-no-underline hover:tw-no-underline">
                                    {artistName.substring(0, 40)}
                                    {' '}
                                    {artistName.trim().length > 40 ? '...' : ''}
                                </div>
                            </React.Fragment>
                        )}
                    </div>

                    <div className="tw-p-4">
                        {!musicTrack ? (
                            <Button className="previewItunes" title={`Sem faixa para ${musicName}`} disabled>
                                <Prohibit size={40} weight="light" />
                            </Button>
                        ) : (
                            <Button
                                className="previewItunes"
                                title={play ? `Pausar ${musicName}` : `Reproduzir ${musicName}`}
                                onClick={(event) => {
                                    event.stopPropagation();

                                    play ? handlingPause() : handlingPlay();
                                }}
                            >
                                {
                                    play ? (
                                        <PauseCircle size={40} weight="light" />
                                    ) : (
                                        <PlayCircle size={40} weight="light" />
                                    )
                                }
                            </Button>
                        )}
                    </div>
                </div>

                {showDownloadButton && (
                    <div>
                        <DownloadButton fileType="music" fileId={`${musicSlug}+${artistSlug}`} />
                    </div>
                )}
            </div>
        </article>
    )
}

export default MusicsCard;

MusicsCard.propTypes = exact({
    currentMusicId: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    playMediaPlayer: PropTypes.func.isRequired,
    pauseMediaPlayer: PropTypes.func.isRequired,
    setPlaylist: PropTypes.func.isRequired,
    playerStatus: PropTypes.string.isRequired,
    playlistType: PropTypes.string.isRequired,
    isLive: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    useFilteredMusics: PropTypes.bool,
    playlistData: PropTypes.arrayOf(PropTypes.object).isRequired,
    filteredMusics: PropTypes.arrayOf(PropTypes.object),
    music: PropTypes.shape({
        musicName: PropTypes.string.isRequired,
        musicId: PropTypes.number,
        musicSlug: PropTypes.string,
        musicTrack: PropTypes.string,
        albumName: PropTypes.string,
        albumCover: PropTypes.string,
        itunesCover: PropTypes.string,
        artistName: PropTypes.string.isRequired,
        artistSlug: PropTypes.string,
    }).isRequired,
    showDownloadButton: PropTypes.bool,
    musicIndex: PropTypes.number,
});
